import * as Sentry from '@sentry/vue';

/**
 * Run work that follows a SUCCESSFUL mutation (analytics, data refresh) so
 * that a failure in it can never be mistaken for a failure of the mutation.
 *
 * Why this exists (#1980): the commit/purchase handlers used to do their
 * tracking and refresh inside the same `try` as the payment API call. When the
 * response shape drifted (an unwrapped resource read as `{ data }`), the
 * tracking line threw, the payment `catch` reported it to Sentry tagged as the
 * payment operation, and the user was told "Failed to commit." for a
 * commitment that had in fact been created and would be charged. A user who
 * believes a payment failed retries it — the worst possible outcome of a
 * cosmetic bug.
 *
 * So mutation handlers wrap ONLY the API call in their error-reporting
 * `try`/`catch`, and route everything after it through here. Failures are
 * still reported — tagged `<operation>_post_success`, so they are never
 * counted against the mutation op — but they never reject and never reach the
 * user as an error.
 *
 * Call it once per independent unit of work (tracking, refresh) so a throw in
 * one does not skip the other: a broken pixel must not also leave the page
 * showing stale state.
 */
export async function runPostSuccess(
  operation: string,
  eventId: string,
  work: () => unknown,
): Promise<void> {
  try {
    await work();
  } catch (e) {
    Sentry.captureException(e, {
      tags: { operation: `${operation}_post_success`, event_id: eventId },
    });
  }
}
